import {
  EventType,
  IncrementalSource,
  serializedNode,
  attributes,
  SbRrwebEvent,
} from 'sb-lib/types';
import { Replayer } from 'sb-lib/rrweb-lib';

const PROXY_URL = `${window.location.origin}/asset-proxy`;

/** Mutatively modify an rrweb event to replace asset URLs with proxied URLs */
export function rrwebEventProxyReplace(
  event: SbRrwebEvent,
  replayer: Replayer,
): SbRrwebEvent {
  if (event.type === EventType.FullSnapshot) {
    if ('childNodes' in event.data.node) {
      recursivelyProxyNodeAttributes(event.data.node);
    }
  }
  if (event.type === EventType.IncrementalSnapshot) {
    const mirror = replayer.getMirror();
    if (event.data.source === IncrementalSource.Mutation) {
      event.data.attributes.forEach(attr => {
        const node = mirror.getNode(attr.id);
        if (node && 'tagName' in node.__sn) {
          // Fudge typing due to implicit connection between node and attr
          attr.attributes = proxyNodeAttributes(
            node.__sn.tagName,
            attr.attributes as attributes,
          ) as any;
        }
      });
      event.data.adds.forEach(add => recursivelyProxyNodeAttributes(add.node));
    }
  }
  return event;
}

/** Lookup of tagName: attribute[] to rewrite URLs to proxy. */
const proxyTagAttributes: Record<string, string[] | undefined> = {
  link: ['href'],
  use: ['xlink:href'],
};

/** Given an rrweb serialized node, recursively mutate attributes with proxy URLs. */
function recursivelyProxyNodeAttributes(node: serializedNode) {
  if ('tagName' in node) {
    node.attributes = proxyNodeAttributes(node.tagName, node.attributes);
  }
  if ('childNodes' in node && node.childNodes.length) {
    node.childNodes.forEach(recursivelyProxyNodeAttributes);
  }
}

/** Return proxied attributes given a tagName and map of attributes */
function proxyNodeAttributes(tagName: string, attributes: attributes): attributes {
  const proxyAttrs = proxyTagAttributes[tagName];
  if (!proxyAttrs) {
    return attributes;
  }
  return Object.entries(attributes).reduce((prev, [attr, val]) => {
    prev[attr] = proxyAttrs.includes(attr) ? proxyAttribute(val) : val;
    return prev;
  }, {} as typeof attributes);
}

function proxyAttribute(url: string | number | boolean) {
  if (typeof url !== 'string') {
    return url;
  }
  // Don't encode #, we want to preserve that for SVGs.
  const [urlBase, hash] = url.split('#');
  const encodedUrl = `${encodeURIComponent(urlBase)}${hash ? `#${hash}` : ''}`;
  return `${PROXY_URL}?${encodedUrl}`;
}
